import {
  afterNextRender,
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { TagComponent } from '@app/shared/components/tag/tag.component';
import { Tag } from '@app/shared/types/models/tag/tag.model';
import { TranslatePipe } from '@ngx-translate/core';
import { resolveVisibleTagCount } from '../task-table-tags.util';

/**
 * @summary Task-table tags that show as many chips as fit and a "+N" overflow count.
 * @selector proffeo-task-table-tags
 */
@Component({
  selector: 'proffeo-task-table-tags',
  templateUrl: './task-table-tags.component.html',
  styleUrl: './task-table-tags.component.css',
  imports: [MatTooltip, TagComponent, TranslatePipe]
})
export class TaskTableTagsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly visibleCount = signal(0);
  private readonly firstTagTruncated = signal(false);

  protected readonly visibleTags = computed(() => this.tags().slice(0, this.visibleCount()));
  protected readonly hiddenCount = computed(() => Math.max(0, this.tags().length - this.visibleCount()));
  /** Some tag is hidden behind the "+N" badge or cut by the column edge. */
  protected readonly isClipped = computed(() => this.hiddenCount() > 0 || this.firstTagTruncated());
  /** Every tag name, shown as a tooltip while the cell cannot show them all. */
  protected readonly allTagNames = computed(() =>
    this.tags()
      .map(tag => tag.name)
      .join(', ')
  );

  public readonly tags = input.required<readonly Tag[]>();

  public constructor() {
    afterRenderEffect(() => {
      this.tags();
      this.measure();
    });
    afterNextRender(() => {
      const view = this.hostElement.nativeElement.ownerDocument.defaultView;
      if (!view) {
        return;
      }

      const observer = new view.ResizeObserver(() => this.measure());
      observer.observe(this.hostElement.nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  private measure(): void {
    const host = this.hostElement.nativeElement;
    const measureRow = host.querySelector<HTMLElement>('.task-table-tags-measure');
    if (!measureRow) {
      this.visibleCount.set(this.tags().length);
      return;
    }

    const overflow = measureRow.querySelector<HTMLElement>('.task-table-tags-more');
    const tagWidthsPx = Array.from(measureRow.querySelectorAll<HTMLElement>('proffeo-tag')).map(
      tag => tag.getBoundingClientRect().width
    );
    const styles = host.ownerDocument.defaultView?.getComputedStyle(measureRow);
    const gapPx = styles ? Number.parseFloat(styles.columnGap || styles.gap || '0') : 0;
    this.firstTagTruncated.set((tagWidthsPx[0] ?? 0) > host.clientWidth);
    this.visibleCount.set(
      resolveVisibleTagCount(
        tagWidthsPx,
        Number.isFinite(gapPx) ? gapPx : 0,
        overflow?.offsetWidth ?? 0,
        host.clientWidth
      )
    );
  }
}
